"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useRef, useState } from "react";
import {
    ChevronsLeft,
    ChevronsRight,
    ExternalLink,
    GripVertical,
    Minimize2,
    X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { usePrismAccess } from "@/lib/flags/usePrismAccess";
import { usePrismDock } from "@/lib/prism/dock/PrismDockContext";
import { isPrismDockHiddenPath } from "@/lib/prism/dock/visibility";
import { usePageContext } from "@/lib/prism/page-context/PageContextProvider";
import { usePrismCatalog } from "@/modules/prism/hooks/usePrismCatalog";
import { usePrismState } from "@/modules/prism/hooks/usePrismState";
import { CompanyScopePicker } from "./CompanyScopePicker";
import { PrismChatPanel } from "./PrismChatPanel";
import type { ComposerAttachment } from "./PrismComposer";
import { SaviraMark } from "./SaviraMark";

export function PrismDockWidget() {
    const pathname = usePathname();
    const { ready, enabled } = usePrismAccess();
    const { activeCompany } = useAuth();
    const {
        mode,
        drawerWidth,
        openDock,
        closeDock,
        minimizeDock,
        setDrawerWidth,
        commitDrawerWidth,
        clampDrawerWidth,
        toggleExpanded,
        isExpanded,
        isSmallScreen,
        seedMessage,
        clearSeedMessage,
    } = usePrismDock();

    const prism = usePrismState();
    const { catalog } = usePrismCatalog();
    const pageContext = usePageContext();
    const companyHint = activeCompany?.name;

    const [input, setInput] = useState("");
    const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
    const [activeSkillId, setActiveSkillId] = useState<string | null>(null);
    const resizeRef = useRef<{ startX: number; startWidth: number; latest: number } | null>(null);

    const hidden = isPrismDockHiddenPath(pathname);
    const showFab = mode === "closed" || mode === "minimized";
    const showDrawer = mode === "open-docked";
    const composerInput = seedMessage ?? input;

    const handleInputChange = useCallback(
        (value: string) => {
            if (seedMessage) clearSeedMessage();
            setInput(value);
        },
        [seedMessage, clearSeedMessage],
    );

    const handleAttachmentsChange = useCallback(
        (value: ComposerAttachment[]) => {
            if (seedMessage) clearSeedMessage();
            setAttachments(value);
        },
        [seedMessage, clearSeedMessage],
    );

    const handleActiveSkillChange = useCallback(
        (value: string | null) => {
            if (seedMessage) clearSeedMessage();
            setActiveSkillId(value);
        },
        [seedMessage, clearSeedMessage],
    );

    const onResizePointerDown = useCallback(
        (e: React.PointerEvent) => {
            e.preventDefault();
            resizeRef.current = { startX: e.clientX, startWidth: drawerWidth, latest: drawerWidth };
            // Avoid text selection / iframe pointer capture jank during drag.
            document.body.style.userSelect = "none";
            document.body.style.cursor = "col-resize";
            const onMove = (ev: PointerEvent) => {
                if (!resizeRef.current) return;
                const delta = resizeRef.current.startX - ev.clientX;
                const next = clampDrawerWidth(resizeRef.current.startWidth + delta);
                resizeRef.current.latest = next;
                setDrawerWidth(next);
            };
            const onUp = () => {
                const latest = resizeRef.current?.latest;
                resizeRef.current = null;
                document.body.style.userSelect = "";
                document.body.style.cursor = "";
                if (latest != null) commitDrawerWidth(latest);
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
            };
            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
        },
        [drawerWidth, setDrawerWidth, commitDrawerWidth, clampDrawerWidth],
    );

    if (!ready || !enabled || hidden) {
        return null;
    }

    return (
        <>
            {showFab ? (
                <div className="fixed bottom-8 right-8 z-[9998]">
                    <motion.button
                        type="button"
                        whileHover={{ scale: 1.06 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => openDock()}
                        className="w-14 h-14 bg-navy hover:bg-navy/90 rounded-full shadow-[var(--shadow-pop)] flex items-center justify-center transition-colors"
                        aria-label="Open Prism assistant"
                    >
                        <SaviraMark className="h-7 w-auto" />
                    </motion.button>
                </div>
            ) : null}

            <AnimatePresence>
                {showDrawer && isSmallScreen ? (
                    <motion.div
                        key="prism-dock-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="fixed inset-0 z-[9998] bg-black/30 backdrop-blur-[1px]"
                        onClick={closeDock}
                        aria-hidden="true"
                    />
                ) : null}

                {showDrawer ? (
                    <motion.aside
                        key="prism-dock"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 28, stiffness: 320 }}
                        className="fixed top-0 right-0 z-[9999] flex h-full flex-col border-l border-[var(--border-hairline)] bg-surface-0 shadow-2xl"
                        style={{ width: isSmallScreen ? "100vw" : clampDrawerWidth(drawerWidth) }}
                    >
                        {!isSmallScreen ? (
                            <div
                                role="separator"
                                aria-orientation="vertical"
                                aria-label="Resize Prism panel"
                                onPointerDown={onResizePointerDown}
                                onDoubleClick={toggleExpanded}
                                className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-accent-line/60 flex items-center justify-center group z-10"
                            >
                                <GripVertical className="w-3 h-3 text-ink-3 opacity-0 group-hover:opacity-100" />
                            </div>
                        ) : null}

                        <header className="flex items-center gap-2 border-b border-[var(--border-hairline)] px-3 py-2.5 shrink-0 bg-surface-0">
                            <SaviraMark className="h-5 w-auto shrink-0" />
                            <span className="text-sm font-semibold text-ink-0 flex-1 truncate">
                                Prism
                            </span>
                            {!isSmallScreen ? (
                                <button
                                    type="button"
                                    onClick={toggleExpanded}
                                    className="p-1.5 rounded-lg text-ink-3 hover:bg-surface-1 hover:text-accent transition-colors"
                                    title={isExpanded ? "Collapse panel" : "Expand panel"}
                                    aria-label={isExpanded ? "Collapse panel" : "Expand panel"}
                                >
                                    {isExpanded ? (
                                        <ChevronsRight className="w-4 h-4" />
                                    ) : (
                                        <ChevronsLeft className="w-4 h-4" />
                                    )}
                                </button>
                            ) : null}
                            <Link
                                href="/modules/prism"
                                className="p-1.5 rounded-lg text-ink-3 hover:bg-surface-1 hover:text-accent transition-colors"
                                title="Open full Prism workspace"
                                aria-label="Open full Prism workspace"
                            >
                                <ExternalLink className="w-4 h-4" />
                            </Link>
                            <button
                                type="button"
                                onClick={minimizeDock}
                                className="p-1.5 rounded-lg text-ink-3 hover:bg-surface-1 hover:text-ink-0 transition-colors"
                                aria-label="Minimize to button"
                            >
                                <Minimize2 className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={closeDock}
                                className="p-1.5 rounded-lg text-ink-3 hover:bg-surface-1 hover:text-ink-0 transition-colors"
                                aria-label="Close Prism"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </header>

                        <CompanyScopePicker
                            scope={prism.state.scope}
                            onScopeChange={prism.setScope}
                        />

                        <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
                            <PrismChatPanel
                                prism={prism}
                                catalog={catalog}
                                companyHint={companyHint}
                                input={composerInput}
                                onInputChange={handleInputChange}
                                attachments={attachments}
                                onAttachmentsChange={handleAttachmentsChange}
                                activeSkillId={activeSkillId}
                                onActiveSkillChange={handleActiveSkillChange}
                                pageContext={pageContext}
                            />
                        </div>
                    </motion.aside>
                ) : null}
            </AnimatePresence>
        </>
    );
}
